<template>
  <section class="section section-profile">
    <div class="tech-bg">
      <div class="grid-overlay"></div>
      <div class="floating-particles">
        <div class="particle" v-for="n in 30" :key="n" :style="particleStyle()"></div>
      </div>
      <div class="glow-orb glow-orb-1"></div>
      <div class="glow-orb glow-orb-2"></div>
      <div class="glow-orb glow-orb-3"></div>
      <div class="hex-grid"></div>
    </div>
    <div class="container">
      <div class="profile-header">
        <h1>个人中心</h1>
        <p>管理您的账户信息和订阅</p>
      </div>

      <div class="profile-content">
        <!-- 用户信息卡片 -->
        <div class="profile-card user-info-card">
          <div class="card-title">
            <i class="el-icon-user"></i>
            账户信息
          </div>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">邮箱：</span>
              <span class="value">{{ userInfo.email || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="label">会员等级：</span>
              <span class="value">{{ subscription.plan_name || '免费版' }}</span>
            </div>
          </div>
        </div>

        <!-- 普通会员卡片 -->
        <div class="profile-card subscription-card">
          <div class="card-title">
            <i class="el-icon-medal"></i>
            当前待遇
          </div>

          <div v-if="loadingSubscription" class="loading-box">
            <i class="el-icon-loading"></i> 加载中...
          </div>

          <div v-else-if="subscription.has_subscription" class="sub-active">
            <div class="sub-badge active">
              <span class="badge-dot"></span>
              已订阅
            </div>
            <div class="sub-details">
              <div class="detail-row">
                <span class="label">当前方案：</span>
                <span class="value plan-name">{{ subscription.plan_name }}</span>
              </div>
              <div class="detail-row">
                <span class="label">剩余次数：</span>
                <span class="value quota">
                  {{ subscription.monthly_quota === -1 ? '无限' : (subscription.remaining_quota || 0) + ' 次' }}
                </span>
              </div>
              <div class="detail-row">
                <span class="label">已用次数：</span>
                <span class="value">{{ subscription.used_quota || 0 }} 次</span>
              </div>
              <div class="detail-row">
                <span class="label">到期时间：</span>
                <span class="value expire">{{ formatDate(subscription.expires_at) }}</span>
              </div>
            </div>
            <div class="sub-actions">
              <button class="btn-renew" @click="renewSubscription">续费</button>
              <button class="btn-upgrade" @click="goToPrice">升级方案</button>
            </div>
          </div>

          <div v-else class="sub-free">
            <div class="sub-free-layout">
              <div class="sub-details">
                <div class="detail-row">
                  <span class="label">每日免费次数：</span>
                  <span class="value">{{ subscription.daily_limit || 0 }} 次</span>
                </div>
                <div class="detail-row highlight">
                  <span class="label">今天还能生成：</span>
                  <span class="value remaining-count">{{ todayRemaining }} 次</span>
                </div>
                <div class="detail-row">
                  <span class="label">今日已用：</span>
                  <span class="value">{{ todayUsed || 0 }} 次</span>
                </div>
                <div class="detail-row">
                  <span class="label">可用模型：</span>
                  <span class="value">{{ getModelsText(subscription.models_access) }}</span>
                </div>
              </div>
              <div class="ad-section" v-if="adEnabled">
                <div class="ad-card" v-if="canWatchAd && maxAdsToday < dailyMaxAds">
                  <div class="ad-icon"><i class="el-icon-video-play"></i></div>
                  <div class="ad-info">
                    <div class="ad-title">观看广告 +1次生成机会</div>
                    <div class="ad-desc">今日次数已用完，看广告可继续生成（今日已看 {{ maxAdsToday }}/{{ dailyMaxAds }} 次）</div>
                  </div>
                  <button class="btn-ad" @click="watchAd" :disabled="loadingAd">
                    {{ loadingAd ? '加载中...' : '立即观看' }}
                  </button>
                </div>
                <div class="ad-card exhausted" v-else-if="maxAdsToday >= dailyMaxAds">
                  <div class="ad-icon exhausted-icon"><i class="el-icon-info"></i></div>
                  <div class="ad-info">
                    <div class="ad-title exhausted-title">今日广告次数已用完</div>
                    <div class="ad-desc">今日已看 {{ maxAdsToday }}/{{ dailyMaxAds }} 次广告，欢迎明天再来</div>
                  </div>
                </div>
                <div class="ad-card no-ads" v-else-if="todayRemaining > 0">
                  <div class="ad-icon exhausted-icon"><i class="el-icon-warning"></i></div>
                  <div class="ad-info">
                    <div class="ad-title exhausted-title">今日次数充足</div>
                    <div class="ad-desc">您还有 {{ todayRemaining }} 次生成机会，无需观看广告</div>
                  </div>
                </div>
                <div class="ad-card exhausted" v-else>
                  <div class="ad-icon exhausted-icon"><i class="el-icon-info"></i></div>
                  <div class="ad-info">
                    <div class="ad-title exhausted-title">今日生成次数已耗尽</div>
                    <div class="ad-desc">广告功能暂未开放，欢迎明天再来</div>
                  </div>
                </div>
              </div>
              <div class="ad-section-empty" v-else>
                <div class="empty-tip">暂无广告任务</div>
              </div>
            </div>
            <div class="sub-actions">
              <button class="btn-upgrade primary" @click="goToPrice">立即升级</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 广告观看弹窗 -->
      <div class="ad-dialog-mask" v-show="adDialogVisible" @click.self="closeAdDialog">
        <div class="ad-dialog">
          <div class="ad-dialog-header">
            <span class="ad-title-icon"><i class="el-icon-video-play"></i></span>
            <span>广告播放中</span>
          </div>
          <div class="ad-dialog-body">
            <div class="ad-countdown-ring">
              <svg viewBox="0 0 100 100" class="countdown-svg">
                <circle cx="50" cy="50" r="45" class="countdown-bg"></circle>
                <circle cx="50" cy="50" r="45" class="countdown-progress" 
                  :style="{ strokeDashoffset: countdownDashoffset }"></circle>
              </svg>
              <div class="countdown-number">
                {{ adCountdown > 0 ? adCountdown : '✓' }}
              </div>
            </div>
            <p class="ad-dialog-tip">{{ adCanClose ? '广告播放完毕，可以关闭了' : '请等待广告播放完毕' }}</p>
          </div>
          <div class="ad-dialog-footer">
            <button class="btn-close-ad" 
              :class="{ 'can-close': adCanClose }"
              :disabled="!adCanClose"
              @click="closeAdDialog">
              {{ adCanClose ? '关闭并获取次数' : `请等待 ${adCountdown}s` }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Profile',
  data() {
    return {
      userInfo: {},
      subscription: {
        has_subscription: false,
        plan_name: '免费版',
        remaining_quota: 0,
        monthly_quota: 0,
        used_quota: 0,
        daily_limit: 0,
        expires_at: null,
        models_access: 'basic'
      },
      todayRemaining: 0,
      todayUsed: 0,
      adEnabled: false,
      canWatchAd: false,
      adDuration: 5,
      dailyMaxAds: 3,
      maxAdsToday: 0,
      loadingSubscription: false,
      loadingAd: false,
      adDialogVisible: false,
      adCountdown: 5,
      adCanClose: false,
      adTimer: null
    }
  },
  mounted() {
    this.loadUserInfo()
    this.loadSubscription()
  },
  computed: {
    countdownDashoffset() {
      const circumference = 2 * Math.PI * 45
      const progress = this.adCountdown / this.adDuration
      return circumference * (1 - progress)
    }
  },
  methods: {
    particleStyle() {
      const size = Math.random() * 4 + 1
      const left = Math.random() * 100
      const top = Math.random() * 100
      const duration = Math.random() * 20 + 10
      const delay = Math.random() * 10
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        top: `${top}%`,
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`
      }
    },
    loadUserInfo() {
      const user = localStorage.getItem('userInfo')
      if (user) {
        this.userInfo = JSON.parse(user)
      }
    },
    async loadSubscription() {
      if (!this.userInfo.id) return
      this.loadingSubscription = true
      try {
        const response = await axios.get('/api/user/subscription', {
          params: { user_id: this.userInfo.id }
        })
        if (response.data.code === 200) {
          const data = response.data.data
          this.subscription = {
            ...data,
            models_access: data.permissions ? data.permissions.models_access : 'basic'
          }
        }
      } catch (error) {
        console.error('加载订阅状态失败:', error)
      } finally {
        this.loadingSubscription = false
      }
      this.loadTodayUsage()
    },
    async loadTodayUsage() {
      if (!this.userInfo.id) return
      try {
        const response = await axios.get('/api/user/today-usage', {
          params: { user_id: this.userInfo.id }
        })
        if (response.data.code === 200) {
          const data = response.data.data
          this.todayRemaining = data.today_remaining
          this.todayUsed = data.today_used
          this.adEnabled = data.ad_enabled
          this.canWatchAd = data.can_watch_ad
          this.adDuration = data.ad_duration || 5
          this.dailyMaxAds = data.daily_max_ads || 3
          this.maxAdsToday = data.max_ads_today || 0
        }
      } catch (error) {
        console.error('加载今日使用情况失败:', error)
      }
    },
    async watchAd() {
      if (this.loadingAd) return
      if (this.maxAdsToday >= this.dailyMaxAds) {
        this.$message.warning(`今日广告次数已达上限（${this.dailyMaxAds}次），明天再来吧`)
        return
      }
      this.adDialogVisible = true
      this.adCountdown = this.adDuration
      this.adCanClose = false
      this.startAdCountdown()
    },
    startAdCountdown() {
      this.adTimer = setInterval(() => {
        this.adCountdown--
        if (this.adCountdown <= 0) {
          clearInterval(this.adTimer)
          this.adCanClose = true
        }
      }, 1000)
    },
    async closeAdDialog() {
      if (!this.adCanClose) return
      this.adDialogVisible = false
      this.loadingAd = true
      try {
        const response = await axios.post('/api/user/watch-ad', null, {
          params: { user_id: this.userInfo.id }
        })
        if (response.data.code === 200) {
          this.$message.success(response.data.data.message || '观看成功，获得1次生成机会')
          this.todayRemaining = response.data.data.today_remaining
          this.canWatchAd = false
        } else {
          this.$message.error(response.data.message || '获取次数失败')
        }
      } catch (error) {
        this.$message.error('获取次数失败')
      } finally {
        this.loadingAd = false
      }
    },
    getModelsText(modelsAccess) {
      const map = {
        'basic': '免费模型',
        'all': '全部模型'
      }
      return map[modelsAccess] || modelsAccess || '-'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    goToPrice() {
      this.$router.push('/price')
    },
    renewSubscription() {
      if (!this.subscription.has_subscription) return
      this.$router.push({
        path: '/subscribe',
        query: {
          plan: this.subscription.plan_name,
          price: this.subscription.plan_name.includes('年') ? 599 : 59,
          period: this.subscription.plan_name.includes('年') ? '年' : '月',
          planId: this.subscription.plan_name.includes('年') ? 'yearly' : 'monthly'
        }
      })
    }
  }
}
</script>

<style scoped>
.section-profile {
  padding: 120px 0 60px;
  background: linear-gradient(180deg, #0a0e1a 0%, #0d1224 50%, #0a0e1a 100%);
  min-height: 100vh;
  position: relative;
}

.tech-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.grid-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 230, 172, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 230, 172, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

.floating-particles {
  position: absolute;
  width: 100%;
  height: 100%;
}

.particle {
  position: absolute;
  background: rgba(0, 230, 172, 0.4);
  border-radius: 50%;
  animation: floatParticle linear infinite;
  filter: blur(1px);
  box-shadow: 0 0 8px rgba(0, 230, 172, 0.3);
}

@keyframes floatParticle {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100vh) translateX(50px); opacity: 0; }
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.15;
}

.glow-orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(0, 204, 153, 0.6) 0%, transparent 70%);
  top: 20%;
  left: 10%;
  animation: floatOrb1 15s ease-in-out infinite;
}

.glow-orb-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 230, 172, 0.6) 0%, transparent 70%);
  top: 50%;
  right: 15%;
  animation: floatOrb2 18s ease-in-out infinite;
}

.glow-orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(79, 172, 254, 0.6) 0%, transparent 70%);
  bottom: 10%;
  left: 50%;
  animation: floatOrb3 20s ease-in-out infinite;
}

@keyframes floatOrb1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(100px, -50px); }
}

@keyframes floatOrb2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-80px, 80px); }
}

@keyframes floatOrb3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-60px, -100px); }
}

.hex-grid {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2300e6ac' fill-opacity='0.02'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.4;
}

.container {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 24px;
}

.profile-header {
  text-align: center;
  margin-bottom: 40px;
}

.profile-header h1 {
  font-size: 36px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
}

.profile-header p {
  font-size: 16px;
  color: #8892b0;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.profile-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title i {
  color: #00e6ac;
}

/* 用户信息 */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

.info-item .label {
  color: #8892b0;
  font-size: 14px;
  margin-right: 8px;
}

.info-item .value {
  color: #e6f1ff;
  font-size: 14px;
}

/* 订阅卡片 */
.sub-active, .sub-free {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sub-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  width: fit-content;
}

.sub-badge.active {
  background: rgba(0, 230, 172, 0.15);
  color: #00e6ac;
  border: 1px solid rgba(0, 230, 172, 0.3);
}

.sub-badge.free {
  background: rgba(255, 255, 255, 0.05);
  color: #8892b0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.sub-badge.active .badge-dot {
  background: #00e6ac;
}

.sub-badge.free .badge-dot {
  background: #8892b0;
}

.sub-active .sub-details,
.sub-free-layout .sub-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sub-free-layout {
  display: flex;
  gap: 20px;
  align-items: stretch;
}

.sub-free-layout .sub-details {
  flex: 0 0 220px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.detail-row .label {
  color: #8892b0;
  width: 100px;
  flex-shrink: 0;
}

.detail-row .value {
  color: #e6f1ff;
}

.detail-row .value.plan-name {
  color: #00e6ac;
  font-weight: 600;
}

.detail-row .value.quota {
  color: #00e6ac;
  font-weight: 600;
  font-size: 16px;
}

.detail-row .value.expire {
  color: #ffd700;
}

.detail-row.highlight .value.remaining-count {
  color: #00e6ac;
  font-weight: 700;
  font-size: 18px;
}

/* 广告区域 */
.ad-section {
  flex: 1;
  min-height: 140px;
}

.ad-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.08) 0%, rgba(255, 170, 0, 0.05) 100%);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 12px;
  height: 100%;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.ad-card:hover {
  border-color: rgba(255, 215, 0, 0.4);
  box-shadow: 0 4px 20px rgba(255, 215, 0, 0.1);
}

.ad-card.exhausted {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.ad-card.exhausted:hover {
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: none;
}

.ad-card.no-ads {
  background: linear-gradient(135deg, rgba(0, 230, 172, 0.05) 0%, rgba(0, 230, 172, 0.02) 100%);
  border: 1px solid rgba(0, 230, 172, 0.15);
}

.ad-card.no-ads:hover {
  border-color: rgba(0, 230, 172, 0.25);
  box-shadow: none;
}

.ad-card.no-ads .ad-icon {
  background: linear-gradient(135deg, rgba(0, 230, 172, 0.15) 0%, rgba(0, 230, 172, 0.08) 100%);
  color: #00e6ac;
}

.ad-card.no-ads .ad-title {
  color: #00e6ac;
}

.ad-card.no-ads .ad-desc {
  color: rgba(0, 230, 172, 0.5);
}

.ad-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 170, 0, 0.15) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #ffd700;
  flex-shrink: 0;
}

.exhausted-icon {
  background: linear-gradient(135deg, rgba(136, 146, 176, 0.15) 0%, rgba(136, 146, 176, 0.08) 100%);
  color: #8892b0;
}

.ad-info {
  flex: 1;
}

.ad-title {
  font-size: 15px;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 4px;
}

.exhausted-title {
  color: #8892b0;
  font-weight: 500;
}

.ad-desc {
  font-size: 12px;
  color: rgba(255, 215, 0, 0.6);
}

.ad-card.exhausted .ad-desc {
  color: rgba(136, 146, 176, 0.6);
}

.btn-ad {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #ffd700, #ffaa00);
  color: #1a1a2e;
  border: none;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-ad:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
}

.btn-ad:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ad-section-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

.empty-tip {
  font-size: 13px;
  color: rgba(136, 146, 176, 0.5);
}

.sub-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.btn-renew, .btn-upgrade {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-renew {
  background: transparent;
  color: #00e6ac;
  border: 1px solid #00e6ac;
}

.btn-renew:hover {
  background: rgba(0, 230, 172, 0.1);
}

.btn-upgrade {
  background: linear-gradient(135deg, #00e6ac, #00c292);
  color: #0a192f;
  border: none;
}

.btn-upgrade:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 230, 172, 0.3);
}

.btn-upgrade.primary {
  width: 100%;
}

/* 广告弹窗 */
.ad-dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.ad-dialog {
  background: linear-gradient(135deg, #1a1f35 0%, #0d1224 100%);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 16px;
  padding: 30px;
  width: 380px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.ad-dialog-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 25px;
}

.ad-title-icon {
  font-size: 22px;
}

.ad-dialog-body {
  margin-bottom: 25px;
}

.ad-countdown-ring {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 20px;
}

.countdown-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.countdown-bg {
  fill: none;
  stroke: rgba(255, 215, 0, 0.1);
  stroke-width: 6;
}

.countdown-progress {
  fill: none;
  stroke: #ffd700;
  stroke-width: 6;
  stroke-linecap: round;
  stroke-dasharray: 282.7;
  transition: stroke-dashoffset 1s linear;
}

.countdown-number {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 36px;
  font-weight: 700;
  color: #ffd700;
}

.ad-dialog-tip {
  font-size: 14px;
  color: #8892b0;
  margin: 0;
}

.ad-dialog-footer {
  margin-top: 10px;
}

.btn-close-ad {
  padding: 12px 30px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: not-allowed;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  width: 100%;
}

.btn-close-ad.can-close {
  cursor: pointer;
  background: linear-gradient(135deg, #ffd700, #ffaa00);
  color: #1a1a2e;
  border: none;
}

.btn-close-ad.can-close:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
}

.btn-close-ad:disabled {
  opacity: 1;
}

/* 加载和空状态 */
.loading-box {
  text-align: center;
  padding: 30px;
  color: #8892b0;
  font-size: 14px;
}

.loading-box i {
  margin-right: 8px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 响应式 */
@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .sub-actions {
    flex-direction: column;
  }

  .btn-renew, .btn-upgrade {
    width: 100%;
    text-align: center;
  }
}
</style>
